"use client";

import { HTMLAttributes } from "react";

type EventColor = "yellow" | "blue" | "green" | "pink" | "orange" | "purple" | "red";

interface EventStickerProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  color?: EventColor;
  rotate?: string;
  /** Show time indicator */
  time?: string;
  /** Visual variant */
  variant?: "default" | "compact";
}

// Uses CSS variables from globals.css for consistency
const colorStyles: Record<EventColor, { bg: string; text: string; defaultRotate: string }> = {
  yellow: {
    bg: "var(--sticky-yellow)",
    text: "var(--sticky-yellow-text)",
    defaultRotate: "-1deg",
  },
  blue: {
    bg: "var(--sticky-blue)",
    text: "var(--sticky-blue-text)",
    defaultRotate: "0.5deg",
  },
  green: {
    bg: "var(--sticky-green)",
    text: "var(--sticky-green-text)",
    defaultRotate: "-0.8deg",
  },
  pink: {
    bg: "var(--sticky-pink)",
    text: "var(--sticky-pink-text)",
    defaultRotate: "1deg",
  },
  orange: {
    bg: "var(--sticky-orange)",
    text: "var(--sticky-orange-text)",
    defaultRotate: "-0.5deg",
  },
  purple: {
    bg: "var(--sticky-purple)",
    text: "var(--sticky-purple-text)",
    defaultRotate: "0.8deg",
  },
  red: {
    bg: "var(--sticky-red)",
    text: "var(--sticky-red-text)",
    defaultRotate: "-0.3deg",
  },
};

export function EventSticker({
  label,
  color = "yellow",
  rotate,
  time,
  variant = "default",
  className = "",
  style,
  ...props
}: EventStickerProps) {
  const colorStyle = colorStyles[color];
  const rotation = rotate || colorStyle.defaultRotate;

  const isCompact = variant === "compact";

  return (
    <div
      className={`
        ${isCompact ? "px-1.5 py-0.5" : "px-2 py-1"}
        rounded-sm text-xs font-medium
        cursor-pointer select-none
        transition-all duration-200
        hover:shadow-md hover:scale-105 hover:rotate-0 hover:z-10
        ${className}
      `}
      style={{
        backgroundColor: colorStyle.bg,
        color: colorStyle.text,
        transform: `rotate(${rotation})`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        transition: "all 0.2s var(--ease-bounce)",
        ...style,
      }}
      {...props}
    >
      <div className="flex items-center gap-1.5">
        {time && (
          <span className="opacity-70 text-[10px] font-normal">{time}</span>
        )}
        <span className="truncate">{label}</span>
      </div>
    </div>
  );
}

export { type EventStickerProps, type EventColor };
